import { resolveNavLink } from './functions';

/**
 * Rescate de los links "de fábrica" del footer.
 *
 * El bug: el default de `Footer` y `FooterModerno` trae anclas (`#financiamiento`,
 * `#about`, …) que NO existen en ninguna página del sitio — ningún componente
 * genera esos `id`. Como nunca hubo forma de editar las columnas del footer
 * desde el panel del builder, esos defaults quedaron intactos en 41 sitios:
 * los 7 links del footer no llevaban a ninguna parte.
 *
 * Acá se traducen SOLO esas anclas conocidas:
 *  - las que tienen una página real (`/about`, `/contact`, `/financing`) se
 *    redirigen a esa ruta;
 *  - las que no tienen página en la plataforma (Garantía, Test Drive, Términos,
 *    Privacidad) se ocultan, porque un link a un 404 es peor que no tenerlo.
 *
 * Es deliberadamente una lista cerrada: cualquier link que el cliente escriba
 * él mismo pasa intacto por `resolveNavLink`. Si mañana existen páginas de
 * términos o garantía, basta moverlas de `LEGACY_SIN_PAGINA` a `LEGACY_A_RUTA`.
 */

/** Anclas de fábrica que sí tienen una página real detrás. */
const LEGACY_A_RUTA: Record<string, string> = {
  '#about': '/about',
  '#nosotros': '/about',
  '#contacto': '/contact',
  '#contact': '/contact',
  '#financiamiento': '/financing',
  '#financing': '/financing',
  '#consignaciones': '/consignments',
  '#consignments': '/consignments',
  '#vehiculos': '/vehicles',
  '#vehicles': '/vehicles',
};

/** Anclas de fábrica sin página en la plataforma: se ocultan. */
const LEGACY_SIN_PAGINA = new Set([
  '#team',
  '#equipo',
  '#garantia',
  '#warranty',
  '#testdrive',
  '#test-drive',
  '#terminos',
  '#terms',
  '#privacidad',
  '#privacy',
]);

export interface FooterLinkTarget {
  href: string;
  isExternal: boolean;
  /** true → no renderizar el link (ancla de fábrica sin destino posible). */
  hidden: boolean;
}

/**
 * ¿Es una de las anclas de fábrica? Devuelve la ruta real, `hidden` si no hay
 * página posible, o `null` si el link es del cliente y no hay que tocarlo.
 * Se expone aparte para los footers que ya normalizan el href con su propia
 * función (p.ej. `normalizeBuilderLink`, que además bloquea `javascript:`).
 */
export const legacyFooterTarget = (
  raw?: string | null
): { ruta: string; hidden: false } | { ruta: null; hidden: true } | null => {
  const key = (raw || '').trim().toLowerCase();
  if (LEGACY_SIN_PAGINA.has(key)) return { ruta: null, hidden: true };
  const ruta = LEGACY_A_RUTA[key];
  return ruta ? { ruta, hidden: false } : null;
};

export const resolveFooterLink = (raw?: string | null): FooterLinkTarget => {
  const legacy = legacyFooterTarget(raw);

  if (legacy?.hidden) return { href: '#', isExternal: false, hidden: true };
  if (legacy) return { href: legacy.ruta, isExternal: false, hidden: false };

  return { ...resolveNavLink(raw), hidden: false };
};
